import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { pdf } from "@react-pdf/renderer";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { tokens } from "../../theme";
import { useAuth } from "../../AuthContext";
import {
  GENERAL_REPORT_CONTENT_OPTIONS,
  REPORT_ASSETS,
  USER_REPORT_CONTENT_OPTIONS,
} from "../../utils/mapers";
import {
  normalizeText,
  preloadImages,
  universityImage,
} from "../../utils/utils";
import GeneralReport from "./GeneralReport";
import ReportDocument from "./ReportTemplate";

function toggleReportContent(currentValues, value) {
  if (value === "general") return ["general"];

  const withoutGeneral = (currentValues || []).filter(
    (item) => item !== "general"
  );

  const nextValues = withoutGeneral.includes(value)
    ? withoutGeneral.filter((item) => item !== value)
    : [...withoutGeneral, value];

  return nextValues.length ? nextValues : ["general"];
}

export default function ReportButton({ persona, data }) {
  const colors = tokens();
  const { user } = useAuth();
  const pdfCacheRef = useRef(new Map());
  const previewUrlRef = useRef(null);

  const [openSelector, setOpenSelector] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const [actionType, setActionType] = useState(null);
  const [selectedMode, setSelectedMode] = useState("general");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [yearFrom, setYearFrom] = useState("");
  const [yearTo, setYearTo] = useState("");
  const [userReportContent, setUserReportContent] = useState(["general"]);
  const [generalReportContent, setGeneralReportContent] = useState(["general"]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState(null);

  const isAdmin = !!user?.is_admin;
  const participantes = data?.tables?.participantes ?? [];
  const userDetailById = data?.index?.userDetailById;

  const participantesOrdenados = useMemo(
    () =>
      [...participantes].sort((a, b) => {
        const byName = String(a?.nombre ?? "").localeCompare(
          String(b?.nombre ?? ""),
          "es",
          { sensitivity: "base" }
        );

        return (
          byName ||
          String(a?.apellido ?? "").localeCompare(
            String(b?.apellido ?? ""),
            "es",
            { sensitivity: "base" }
          )
        );
      }),
    [participantes]
  );

  const selectedUserDetail = useMemo(() => {
    if (!selectedUserId || !userDetailById?.get) return null;
    return userDetailById.get(String(selectedUserId)) ?? null;
  }, [selectedUserId, userDetailById]);

  const ownUserDetail = useMemo(() => {
    if (persona?.id && userDetailById?.get) {
      return userDetailById.get(String(persona.id)) ?? persona;
    }

    return persona ?? null;
  }, [persona, userDetailById]);

  const effectiveUserDetail = isAdmin ? selectedUserDetail : ownUserDetail;

  const availableYears = useMemo(() => {
    const discipline = normalizeText(
      effectiveUserDetail?.disciplina ??
        effectiveUserDetail?.discipline ??
        effectiveUserDetail?.type
    );
    const startYear = discipline === "lenguaje" ? 2025 : 2022;
    const currentYear = new Date().getFullYear();

    return Array.from(
      { length: currentYear - startYear + 1 },
      (_, index) => startYear + index
    );
  }, [effectiveUserDetail]);

  const resetYearRange = () => {
    setYearFrom("");
    setYearTo("");
  };

  useEffect(() => {
    if (!selectedUserId) return;

    const exists = participantesOrdenados.some(
      (participant) => String(participant.id) === String(selectedUserId)
    );

    if (!exists) {
      setSelectedUserId("");
      resetYearRange();
    }
  }, [selectedUserId, participantesOrdenados]);

  useEffect(() => {
    const validYears = new Set(availableYears.map(String));

    if (yearFrom && !validYears.has(String(yearFrom))) setYearFrom("");
    if (yearTo && !validYears.has(String(yearTo))) setYearTo("");
  }, [availableYears, yearFrom, yearTo]);

  useEffect(() => {
    pdfCacheRef.current.clear();

    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
      setPreviewUrl(null);
    }
  }, [data]);

  useEffect(
    () => () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    },
    []
  );

  const safeSelectedUserId = useMemo(
    () =>
      participantesOrdenados.some(
        (participant) => String(participant.id) === String(selectedUserId)
      )
        ? selectedUserId
        : "",
    [participantesOrdenados, selectedUserId]
  );

  const normalizedYearFrom = useMemo(() => {
    if (!yearFrom && !yearTo) return null;
    if (yearFrom && yearTo) return Math.min(Number(yearFrom), Number(yearTo));
    return Number(yearFrom || yearTo);
  }, [yearFrom, yearTo]);

  const normalizedYearTo = useMemo(() => {
    if (!yearFrom && !yearTo) return null;
    if (yearFrom && yearTo) return Math.max(Number(yearFrom), Number(yearTo));
    return Number(yearFrom || yearTo);
  }, [yearFrom, yearTo]);

  const selectedDocument = useMemo(() => {
    if (isAdmin && selectedMode === "general") {
      return <GeneralReport data={data} contentFilter={generalReportContent} />;
    }

    return effectiveUserDetail ? (
      <ReportDocument
        persona={effectiveUserDetail}
        yearFrom={normalizedYearFrom}
        yearTo={normalizedYearTo}
        contentFilter={userReportContent}
      />
    ) : null;
  }, [
    data,
    effectiveUserDetail,
    generalReportContent,
    isAdmin,
    normalizedYearFrom,
    normalizedYearTo,
    selectedMode,
    userReportContent,
  ]);

  const yearSuffix = useMemo(() => {
    if (!normalizedYearFrom && !normalizedYearTo) return "general";
    if (normalizedYearFrom === normalizedYearTo) return `${normalizedYearFrom}`;
    return `${normalizedYearFrom}_${normalizedYearTo}`;
  }, [normalizedYearFrom, normalizedYearTo]);

  const selectedFileName = useMemo(() => {
    if (isAdmin && selectedMode === "general") {
      return "reporte_general_redfid.pdf";
    }

    const baseName =
      effectiveUserDetail?.nombre_completo ||
      `${effectiveUserDetail?.nombre ?? ""}_${
        effectiveUserDetail?.apellido ?? ""
      }`.trim() ||
      "usuario";

    return `informe_${baseName.replace(/\s+/g, "_")}_${yearSuffix}.pdf`;
  }, [effectiveUserDetail, isAdmin, selectedMode, yearSuffix]);

  const canRenderDocument =
    isAdmin && selectedMode === "general" ? true : !!effectiveUserDetail;

  const reportKey = useMemo(
    () =>
      JSON.stringify({
        mode: isAdmin ? selectedMode : "usuario",
        userId: effectiveUserDetail?.id ?? null,
        yearFrom: normalizedYearFrom,
        yearTo: normalizedYearTo,
        userContent: [...userReportContent].sort(),
        generalContent: [...generalReportContent].sort(),
        country: data?.country ?? "chile",
      }),
    [
      data?.country,
      effectiveUserDetail?.id,
      generalReportContent,
      isAdmin,
      normalizedYearFrom,
      normalizedYearTo,
      selectedMode,
      userReportContent,
    ]
  );

  const reportImageSources = useMemo(() => {
    const discipline = normalizeText(effectiveUserDetail?.disciplina);
    const reportAssets = REPORT_ASSETS[
      discipline.includes("lenguaje") ? "lenguaje" : "matematica"
    ];
    const sources = [reportAssets.header, reportAssets.footer];

    if (
      isAdmin &&
      selectedMode === "general" &&
      (generalReportContent.includes("general") ||
        generalReportContent.includes("universidades"))
    ) {
      (data?.tables?.universidades ?? []).forEach((university) => {
        sources.push(
          universityImage(
            university?.universidad_full ?? university?.universidad
          )
        );
      });
    } else if (
      effectiveUserDetail?.rawUser?.profile_image &&
      effectiveUserDetail?.rawUser?.id
    ) {
      sources.push(
        `${process.env.REACT_APP_STATIC_URL}/users/${
          effectiveUserDetail.rawUser.id
        }/${effectiveUserDetail.rawUser.profile_image}`
      );
    }

    return [...new Set(sources.filter(Boolean))];
  }, [
    data?.tables?.universidades,
    effectiveUserDetail,
    generalReportContent,
    isAdmin,
    selectedMode,
  ]);

  const generatePdfBlob = useCallback(async () => {
    if (!selectedDocument) throw new Error("No hay un reporte disponible.");

    if (!pdfCacheRef.current.has(reportKey)) {
      const generation = (async () => {
        await preloadImages(reportImageSources);
        return pdf(selectedDocument).toBlob();
      })();

      pdfCacheRef.current.set(reportKey, generation);
      generation.catch(() => pdfCacheRef.current.delete(reportKey));
    }

    return pdfCacheRef.current.get(reportKey);
  }, [reportImageSources, reportKey, selectedDocument]);

  const handleOpenAction = (type) => {
    setActionType(type);
    setGenerationError(null);

    if (!isAdmin) {
      setSelectedMode("usuario");
      setSelectedUserId(persona?.id ? String(persona.id) : "");
    }

    setOpenSelector(true);
  };

  const handleModeChange = (mode) => {
    setSelectedMode(mode);
    setGenerationError(null);

    if (mode !== "usuario") {
      setSelectedUserId("");
      resetYearRange();
    }
  };

  const preparePreview = async () => {
    setOpenSelector(false);
    setOpenPreview(true);
    setIsGenerating(true);
    setGenerationError(null);

    try {
      const blob = await generatePdfBlob();

      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }

      const url = URL.createObjectURL(blob);
      previewUrlRef.current = url;
      setPreviewUrl(url);
    } catch (error) {
      setGenerationError(error);
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadReport = async () => {
    setIsGenerating(true);
    setGenerationError(null);

    try {
      const blob = await generatePdfBlob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");

      anchor.href = url;
      anchor.download = selectedFileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 0);
      setOpenSelector(false);
    } catch (error) {
      setGenerationError(error);
    } finally {
      setIsGenerating(false);
    }
  };

  const mustSelectUser =
    isAdmin && selectedMode === "usuario" && !safeSelectedUserId;
  const showYearSelectors =
    (!isAdmin && !!ownUserDetail) || (isAdmin && selectedMode === "usuario");
  const showUserReportContent = showYearSelectors;
  const showGeneralReportContent = isAdmin && selectedMode === "general";

  return (
    <>
      <Box display="flex" gap={1}>
        <Button
          onClick={() => handleOpenAction("preview")}
          sx={{
            backgroundColor: colors.green[100],
            color: colors.primary[100],
            fontSize: "14px",
            fontWeight: "bold",
            padding: "10px 20px",
            "&:hover": { backgroundColor: colors.green[200] },
          }}
        >
          <VisibilityOutlinedIcon sx={{ mr: "10px" }} />
          Ver reporte
        </Button>

        <Button
          onClick={() => handleOpenAction("download")}
          sx={{
            backgroundColor: colors.green[100],
            color: colors.primary[100],
            fontSize: "14px",
            fontWeight: "bold",
            padding: "10px 20px",
            "&:hover": { backgroundColor: colors.green[200] },
          }}
        >
          <DownloadOutlinedIcon sx={{ mr: "10px" }} />
          Descargar reporte
        </Button>
      </Box>

      <Dialog
        open={openSelector}
        onClose={() => !isGenerating && setOpenSelector(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {isAdmin ? "Seleccionar reporte" : "Seleccionar período del reporte"}
        </DialogTitle>

        <DialogContent>
          {isAdmin && (
            <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
              <InputLabel id="report-mode-label">Tipo de reporte</InputLabel>
              <Select
                labelId="report-mode-label"
                value={selectedMode}
                label="Tipo de reporte"
                onChange={(event) => handleModeChange(event.target.value)}
              >
                <MenuItem value="general">Reporte general</MenuItem>
                <MenuItem value="usuario">Reporte por usuario</MenuItem>
              </Select>
            </FormControl>
          )}

          {isAdmin && selectedMode === "usuario" && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="user-select-label">Usuario</InputLabel>
              <Select
                labelId="user-select-label"
                value={safeSelectedUserId}
                label="Usuario"
                onChange={(event) => {
                  setSelectedUserId(event.target.value);
                  resetYearRange();
                }}
              >
                {participantesOrdenados.map((participant) => (
                  <MenuItem key={participant.id} value={String(participant.id)}>
                    {`${participant.nombre ?? ""} ${
                      participant.apellido ?? ""
                    }`.trim()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {mustSelectUser && (
            <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
              Selecciona un usuario para continuar.
            </Typography>
          )}

          {showYearSelectors && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Período del reporte de usuario
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <FormControl fullWidth>
                  <InputLabel id="year-from-label">Desde</InputLabel>
                  <Select
                    labelId="year-from-label"
                    value={yearFrom}
                    label="Desde"
                    onChange={(event) => setYearFrom(event.target.value)}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {availableYears.map((year) => (
                      <MenuItem key={`from-${year}`} value={String(year)}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel id="year-to-label">Hasta</InputLabel>
                  <Select
                    labelId="year-to-label"
                    value={yearTo}
                    label="Hasta"
                    onChange={(event) => setYearTo(event.target.value)}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {availableYears.map((year) => (
                      <MenuItem key={`to-${year}`} value={String(year)}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              <Typography variant="body2" sx={{ mt: 1.5, mb: 2 }}>
                Puedes elegir un solo año o un intervalo. Si dejas ambos en
                “Todos”, se generará el reporte completo.
              </Typography>
            </>
          )}

          {showGeneralReportContent && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Contenido del reporte
              </Typography>

              <FormGroup>
                {GENERAL_REPORT_CONTENT_OPTIONS.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    control={
                      <Checkbox
                        checked={generalReportContent.includes(option.value)}
                        onChange={() =>
                          setGeneralReportContent((current) =>
                            toggleReportContent(current, option.value)
                          )
                        }
                        sx={{
                          "&.Mui-checked": { color: colors.green[100] },
                        }}
                      />
                    }
                    label={
                      option.value === "regiones" &&
                      data?.country === "extranjero"
                        ? "Países"
                        : option.label
                    }
                  />
                ))}
              </FormGroup>

              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Selecciona el contenido que deseas incluir en el reporte. Si
                seleccionas “General”, de genera un reporte completo.
              </Typography>
            </Box>
          )}

          {showUserReportContent && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Contenido del reporte
              </Typography>

              <FormGroup>
                {USER_REPORT_CONTENT_OPTIONS.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    control={
                      <Checkbox
                        checked={userReportContent.includes(option.value)}
                        onChange={() =>
                          setUserReportContent((current) =>
                            toggleReportContent(current, option.value)
                          )
                        }
                        sx={{
                          "&.Mui-checked": { color: colors.green[100] },
                        }}
                      />
                    }
                    label={option.label}
                  />
                ))}
              </FormGroup>

              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Selecciona el contenido que deseas incluir en el reporte. Si
                seleccionas “General”, de genera un reporte completo.
              </Typography>
            </Box>
          )}

          {generationError ? (
            <Typography color="error" variant="body2" sx={{ mt: 2 }}>
              No fue posible generar el reporte: {String(generationError.message ?? generationError)}
            </Typography>
          ) : null}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setOpenSelector(false)}
            disabled={isGenerating}
          >
            Cancelar
          </Button>
          <Button
            onClick={actionType === "download" ? downloadReport : preparePreview}
            disabled={mustSelectUser || !canRenderDocument || isGenerating}
          >
            {isGenerating
              ? "Generando..."
              : actionType === "download"
                ? "Descargar"
                : "Continuar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openPreview}
        onClose={() => !isGenerating && setOpenPreview(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            pr: 3,
          }}
        >
          <Typography fontWeight="bold">Vista previa del reporte</Typography>

          <Box display="flex" gap={1}>
            <Button
              onClick={() => setOpenPreview(false)}
              disabled={isGenerating}
            >
              Cerrar
            </Button>
            <Button
              onClick={downloadReport}
              disabled={!previewUrl || isGenerating}
            >
              {isGenerating ? "Generando..." : "Descargar PDF"}
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ height: "85vh", p: 0 }}>
          {isGenerating ? (
            <Box
              height="100%"
              display="grid"
              sx={{ placeItems: "center" }}
            >
              <Typography>Generando reporte...</Typography>
            </Box>
          ) : generationError ? (
            <Box p={3}>
              <Typography color="error">
                No fue posible generar el reporte: {String(generationError.message ?? generationError)}
              </Typography>
            </Box>
          ) : previewUrl ? (
            <Box
              component="iframe"
              title="Vista previa del reporte"
              src={previewUrl}
              width="100%"
              height="100%"
              sx={{ border: 0, display: "block" }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}
